import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/cn';
import type { SubmissionDisplayState } from '../../../lib/submissionDisplayState';

/** @deprecated Prefer SubmissionDisplayState */
export type SubmissionQueueState = 'error' | 'draft' | 'ready';

type SubmissionStatusChipProps = {
  state?: SubmissionQueueState;
  displayState?: SubmissionDisplayState;
  synced?: boolean;
  className?: string;
};

const DISPLAY_TONE: Record<SubmissionDisplayState, string> = {
  ungraded:
    'border-slate-300/60 bg-slate-100/90 text-slate-700 dark:border-slate-600 dark:bg-slate-800/60 dark:text-slate-300',
  draft: 'border-dg-accent-grade/30 bg-dg-accent-grade/10 text-amber-900 dark:text-amber-200',
  finalized: 'border-emerald-500/25 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300',
  missing: 'border-rose-500/25 bg-rose-500/10 text-rose-700 dark:text-rose-300',
  late: 'border-violet-500/25 bg-violet-500/10 text-violet-800 dark:text-violet-300',
  error: 'border-rose-500/25 bg-rose-500/10 text-rose-700 dark:text-rose-300',
};

function queueToDisplay(state: SubmissionQueueState): SubmissionDisplayState {
  if (state === 'ready') return 'finalized';
  if (state === 'error') return 'error';
  return 'draft';
}

export function SubmissionStatusChip({
  state,
  displayState,
  synced = false,
  className,
}: SubmissionStatusChipProps): ReactElement {
  const { t } = useTranslation();
  const resolved = displayState ?? (state ? queueToDisplay(state) : 'ungraded');
  const labelKey = `audit.rowState.${resolved}` as const;
  const label = t(labelKey);

  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <span
        className={cn(
          'inline-flex shrink-0 rounded-full border px-2 py-0.5 text-dg-label font-medium uppercase tracking-wide',
          DISPLAY_TONE[resolved],
          className
        )}
      >
        {label}
      </span>
      {synced ? (
        <span className="inline-flex shrink-0 rounded-full border border-slate-200/80 bg-slate-50 px-2 py-0.5 text-dg-label font-medium uppercase tracking-wide text-slate-600 dark:border-slate-600 dark:bg-slate-800/60 dark:text-slate-300">
          {t('audit.rowState.synced')}
        </span>
      ) : null}
    </span>
  );
}
